// Слои сессий: порядок, раскладка по глубине, медианы, разбор заголовков, события.
// Формулы перенесены из прототипа v14 (tmp/Матрицы_v14_…html); выдуманные данные
// прототипа (vals, storyFor, eventsOf) заменены реальными полями сессии (К2).
import { median } from './wordMatrix';

// День по Москве: граница суток у заказчика в 00:00 МСК, а не UTC.
export const dayOf = ms => Math.floor((ms + 3 * 3600e3) / 864e5);

// Блок оттока из reversals(): только так число синих в блоке совпадает с ch клетки.
// -1 — сессия с разворотом вверх или повтором веса, оттока ей не засчитано.
export const hitOf = u => u.churn;

// Ключи сортировок: номер блока оттока (1..N, без оттока 0) и двоичный «синяя в блоке b».
export const keyHit = u => hitOf(u) + 1;
export const keyBlock = b => u => (hitOf(u) === b ? 1 : 0);

const sgn = dir => (dir === 'desc' || dir === -1 ? -1 : 1);
const byStart = (a, b) => a.start - b.start || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
const ruCmp = (a, b) => String(a).localeCompare(String(b), 'ru');

export function orderByTime(units) {
  return units.slice().sort(byStart);
}

// Карточки одной фразы встают подряд; группы — от самой многочисленной. При сортировке
// по блоку в порядке «по фразам» разворачивается и порядок групп (по числу синих в
// блоке), иначе на группах в одну-две карточки вторая сторона сортировки не видна.
// block передаётся только в режиме «по фразам»: в «фразы + время» группы стоят на месте.
export function orderPhrase(units, block = null, dir = 'asc') {
  const s = sgn(dir);
  const gs = new Map();
  units.forEach(u => {
    if (!gs.has(u.phrase)) gs.set(u.phrase, { ph: u.phrase, items: [], sum: 0 });
    const g = gs.get(u.phrase);
    g.items.push(u);
    if (block !== null) g.sum += hitOf(u) === block ? 1 : 0;
  });
  const order = [...gs.values()].sort(block === null
    ? (a, b) => b.items.length - a.items.length || ruCmp(a.ph, b.ph)
    : (a, b) => ((a.sum - b.sum) || (a.items.length - b.items.length) || ruCmp(a.ph, b.ph)) * s);
  return [].concat(...order.map(g => g.items));
}

// Сортировка в пределах подряд идущих групп фразы (byPhrase) или всего ряда.
function sortRuns(units, cmp, byPhrase) {
  if (!byPhrase) return units.slice().sort(cmp);
  const out = [];
  let i = 0;
  while (i < units.length) {
    let j = i;
    while (j < units.length && units[j].phrase === units[i].phrase) j++;
    out.push(...units.slice(i, j).sort(cmp));
    i = j;
  }
  return out;
}

// Разворот зеркальный целиком, включая второстепенные ключи: иначе равные карточки
// стоят на месте и кажется, что сортировка работает в одну сторону.
export function orderBlock(units, b, dir = 'asc', byPhrase = false) {
  const key = keyBlock(b), s = sgn(dir);
  return sortRuns(units, (x, y) => ((key(x) - key(y)) || byStart(x, y)) * s, byPhrase);
}

// Сессии без оттока в конце при любом направлении: у них нет меры, чтобы встать в ряд.
export function orderHit(units, dir = 'asc', byPhrase = false) {
  const s = sgn(dir);
  return sortRuns(units, (x, y) => ((hitOf(x) < 0) - (hitOf(y) < 0))
    || ((keyHit(x) - keyHit(y)) || byStart(x, y)) * s, byPhrase);
}

// ---- цвета и дорожки фраз ----
// Ранг фразы — по числу сессий, при равенстве по алфавиту: первые шесть получают
// токены темы, остальные — золотой угол по кругу, чтобы соседи не сливались.
function rankPhrases(phrases) {
  const cnt = new Map();
  phrases.forEach(p => cnt.set(p, (cnt.get(p) || 0) + 1));
  const rank = new Map();
  [...cnt.keys()].sort((a, b) => cnt.get(b) - cnt.get(a) || ruCmp(a, b)).forEach((p, i) => rank.set(p, i));
  return rank;
}

export function phraseColors(phrases) {
  const out = Object.create(null);
  rankPhrases(phrases).forEach((i, p) => {
    out[p] = i < 6 ? `var(--ph${i})` : `hsl(${(i * 137.5) % 360} 55% 50%)`;
  });
  return out;
}

// ---- раскладка по глубине: острова ----
const DEPTH = 1250;
const depthFor = n => Math.max(380, Math.min(DEPTH, n * 170));
const squashGap = g => (g <= 3 ? g : 3 + Math.round(Math.sqrt(g - 3) * 2.4));
const monthKey = day => { const d = new Date(day * 864e5); return d.getUTCFullYear() * 12 + d.getUTCMonth(); };

// opts: mode 'time' | 'even' | 'phrase' | 'phtime' (порядок × шаг), dayRow — сессии дня
// в ряд, laneBy — дорожки по фразам, colW — ширина столбца, sorted — ряд переставлен
// сортировкой, spine — корешки, dzEven — шаг вдоль стопки (задаёт сцена по кадру).
// z — глубина вглубь сцены, px ≥ 0; первая карточка на нуле.
export function layoutUnits(units, opts = {}) {
  const mode = opts.mode || 'time';
  const colW = opts.colW || 72;
  const spine = !!opts.spine;
  const sorted = !!opts.sorted;
  const timeLike = mode === 'time' || mode === 'phtime';
  const byPhrase = mode === 'phrase' || mode === 'phtime';
  // при сортировке глубина всегда ровная: порядок задан значением, а не датой
  const evenLike = opts.dzEven != null || mode === 'even' || mode === 'phrase' || sorted;
  const dzEven = opts.dzEven != null ? opts.dzEven
    : Math.max(24, Math.min(200, Math.round(depthFor(units.length) / Math.max(1, units.length))));
  const STEP_SAME = spine ? 36 : 80;    // два захода подряд — разные дни
  const STEP_DUP = spine ? 7 : 16;      // ещё одна сессия в тот же день
  const STEP_DAY = spine ? 12 : 26;     // добавка за каждый день внутри острова
  const ISL_BREAK = 4;                  // с этого разрыва начинается новый остров
  const GAP_MIN = spine ? 150 : 300;    // минимальный провал между островами
  const GRP_GAP = spine ? 210 : 420;    // провал между группами фраз
  const rowOn = !!opts.dayRow && timeLike && !sorted;

  const items = [], islands = [], ticks = [];
  const seenDay = new Set();
  let z = 0, prevDay = null, isl = null, lastMonth = null, evenIdx = 0;
  units.forEach((u, i) => {
    const grpBreak = byPhrase && i > 0 && u.phrase !== units[i - 1].phrase;
    // «фразы + время»: на границе групп отсчёт дат заново, иначе переход читался бы паузой
    if (mode === 'phtime' && grpBreak) {
      z += GRP_GAP; prevDay = null; if (isl) { islands.push(isl); isl = null; }
      evenIdx += 1.7;
    }
    evenIdx += 1;
    let newIsl = prevDay === null;
    if (prevDay !== null) {
      // в переставленном ряду (сортировка) соседние дни идут и назад: разрыв меряем по модулю
      const g = Math.abs(u.day - prevDay);
      if (g >= ISL_BREAK) {
        newIsl = true;
        // длинный простой сжимаем по корню: настоящее число дней стоит в подписи провала
        z += GAP_MIN + Math.round(squashGap(g) * 9);
      } else if (g === 0 && rowOn) {
        // тот же день в режиме «в ряд» — глубину не трогаем, встанем сбоку
      } else if (g === 0) {
        z += STEP_DUP;
      } else {
        z += STEP_SAME + g * STEP_DAY;
      }
    }
    if (newIsl) {
      if (isl) islands.push(isl);
      isl = { from: i, to: i, z0: z, z1: z, day0: u.day, day1: u.day, n: 0 };
    }
    isl.to = i; isl.z1 = z; isl.n += 1;
    isl.day0 = Math.min(isl.day0, u.day); isl.day1 = Math.max(isl.day1, u.day);
    const first = !seenDay.has(u.day);
    seenDay.add(u.day);
    if (first) {
      const mk = monthKey(u.day);
      ticks.push({ z, big: mk !== lastMonth });
      lastMonth = mk;
    }
    prevDay = u.day;
    items.push({ unit: u, i, z: evenLike ? (evenIdx - 1) * dzEven : z, x: 0, lane: 0,
      island: islands.length, first, rowK: 0, rowN: 1 });
  });
  if (isl) islands.push(isl);
  // На ровном шаге (сортировка, вдоль стопки) островок стоит под своими карточками, а не на
  // календарной глубине: у П тут глубина по датам, и подпись уезжала от карточек.
  if (evenLike) islands.forEach(is => { is.z0 = items[is.from].z; is.z1 = items[is.to].z; });

  // Ряд за день растёт только вправо («раньше левее»), и только по НЕПРЕРЫВНЫМ отрезкам
  // одного дня: при порядке «по фразам» день разорван между группами.
  const runs = [];
  units.forEach((u, i) => {
    const last = runs[runs.length - 1];
    if (last && units[last[last.length - 1]].day === u.day) last.push(i);
    else runs.push([i]);
  });
  const rowStep = n => Math.max(34, Math.min(colW + 16, 1500 / n));
  let maxSpan = 0;
  runs.forEach(idxs => {
    const n = idxs.length;
    idxs.forEach((idx, k) => {
      items[idx].rowK = k; items[idx].rowN = n;
      if (rowOn && n > 1) items[idx].x = k * rowStep(n);
    });
    if (rowOn && n > 1) maxSpan = Math.max(maxSpan, (n - 1) * rowStep(n));
  });

  // Дорожки: у каждой фразы своя параллельная полоса вправо от оси среза, по рангу фразы.
  let laneW = 0;
  if (opts.laneBy && units.length) {
    const rank = rankPhrases(units.map(u => u.phrase));
    laneW = Math.max(colW + 72, maxSpan + colW + 40);
    items.forEach(it => { it.lane = rank.get(it.unit.phrase); it.x += it.lane * laneW; });
  }
  const maxDepth = items.length ? items.reduce((m, it) => Math.max(m, it.z), 0) : 300;
  const outTicks = evenLike ? items.map(it => ({ z: it.z, big: false })) : ticks;
  return { items, maxDepth, islands, ticks: outTicks, dzEven, laneW };
}

// ---- медиана в сцене ----
// База: 'col' — все сессии столбца, 'grp' — своя фраза, 'all' — сессии всех слов (opts.all).
// При мере «блок оттока» (keyHit) сессии без оттока меры не имеют и в медиану не входят;
// у двоичной меры keyBlock ноль законен — «без синей в блоке».
const measured = (list, key) => (key === keyHit ? list.filter(u => hitOf(u) >= 0) : list);
export function medFor(units, base, key, opts = {}) {
  const all = opts.all ? measured(opts.all, key) : [];
  if (base === 'all' && all.length) return median(all.map(key));
  if (base === 'grp' && opts.phrase != null) {
    const g = measured(units.filter(u => u.phrase === opts.phrase), key);
    if (g.length) return median(g.map(key));
  }
  return median(measured(units, key).map(key));
}

// Индекс первой карточки, перешедшей медиану в сторону сортировки. На двоичной мере
// медиана вырождается в 0 или 1, поэтому вызывающий передаёт m = 0.5.
// Для keyHit сессии без оттока (хвост после orderHit) вне сравнения: граница не заходит
// за последнюю сессию с оттоком.
export function medSplit(list, m, key, dir = 'asc') {
  const s = sgn(dir);
  let end = 0;
  for (let i = 0; i < list.length; i++) {
    if (key === keyHit && hitOf(list[i]) < 0) continue;
    const v = key(list[i]);
    if (s === 1 ? v > m : v < m) return i;
    end = i + 1;
  }
  return key === keyHit ? end : list.length;
}

// ---- разбор заголовков: единица анализа — уникальная пара «заголовок ¦ подзаголовок» ----
// Мера — номер блока оттока 1..N. Нижняя медиана, квартили ближайшим рангом, среднее
// (медиана на семи блоках слишком крупнозерниста, коромысло держится на среднем).
const anMed = a => { const z = a.slice().sort((x, y) => x - y); return z.length ? z[(z.length - 1) >> 1] : 0; };
const anMean = a => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
const anQ = (a, q) => {
  const z = a.slice().sort((x, y) => x - y);
  return z.length ? z[Math.min(z.length - 1, Math.max(0, Math.round((z.length - 1) * q)))] : 0;
};
const cmpV = (a, b) => (typeof a === 'number' && typeof b === 'number' ? a - b : ruCmp(a, b));

// Доля сессий по блокам [b1..bN], сумма 1.
function histOf(blocks, nBlocks) {
  const h = Array(nBlocks).fill(0);
  blocks.forEach(b => { if (b >= 1 && b <= nBlocks) h[b - 1]++; });
  const s = blocks.length || 1;
  return h.map(v => v / s);
}

// Заголовок сессии — то же правило, что в решётке (headGrid.js buildTree): пустой = !u.title,
// такой идёт служебным текстом «без подмены» (показ известен) или «нет данных», подзаголовок
// его не делит. key различает служебный текст и настоящий заголовок с тем же словом.
export const NO_SWAP = 'без подмены', NO_DATA = 'нет данных';
export function headOf(u) {
  if (u.title) return { t: u.title, s: u.subtitle || '', none: false, key: u.title + ' ¦ ' + (u.subtitle || '') };
  const t = u.shownSource == null ? NO_DATA : NO_SWAP;
  return { t, s: '', none: true, key: t };
}
// Сессия без оттока: reached = -1 значит ни одного блока (R46), иначе отток не засчитан.
export const noChurnTxt = u => (u.reached < 0 ? 'нет блоков' : 'без оттока');

// attr: 'dev' | 'phrase' | 'none' — признак, режущий заголовок на подгруппы.
// Без оттока сессия в разборе не участвует: меры нет. Без заголовка — идёт служебным
// текстом (headOf), как в решётке; служебные строки всегда в хвосте.
export function headStats(units, attr, nBlocks) {
  const groupOf = u => (attr === 'dev' ? u.dev : attr === 'phrase' ? u.phrase : -1);
  const map = new Map();
  const all = [];
  units.forEach(u => {
    if (u.churn < 0) return;
    // разделитель печатный: ключ уезжает в data-атрибут решётки
    const h = headOf(u), k = h.key;
    const hb = u.churn + 1;
    all.push(hb);
    let e = map.get(k);
    if (!e) {
      e = { key: k, t: h.t, s: h.s, none: h.none, ph: u.phrase, phc: Object.create(null),
        blocks: [], g: new Map(), first: map.size };
      map.set(k, e);
    }
    e.blocks.push(hb);
    e.phc[u.phrase] = (e.phc[u.phrase] || 0) + 1;
    const gv = groupOf(u);
    if (!e.g.has(gv)) e.g.set(gv, { v: gv, blocks: [] });
    e.g.get(gv).blocks.push(hb);
  });
  const N = nBlocks || all.reduce((m, v) => Math.max(m, v), 0);
  const M = anMed(all);
  const Mm = anMean(all);
  const list = [...map.values()].map(e => {
    const groups = [...e.g.values()].map(g => ({
      v: g.v, n: g.blocks.length, med: anMed(g.blocks), q1: anQ(g.blocks, 0.25), q3: anQ(g.blocks, 0.75),
      d: anMed(g.blocks) - M, mean: anMean(g.blocks), dm: anMean(g.blocks) - Mm,
      hist: histOf(g.blocks, N),
    })).sort((a, b) => cmpV(a.v, b.v));
    return { key: e.key, t: e.t, s: e.s, none: e.none, ph: e.ph, first: e.first, n: e.blocks.length,
      mix: Object.keys(e.phc).map(k => ({ k, n: e.phc[k] })), phc: e.phc,
      med: anMed(e.blocks), q1: anQ(e.blocks, 0.25), q3: anQ(e.blocks, 0.75),
      d: anMed(e.blocks) - M, mean: anMean(e.blocks), dm: anMean(e.blocks) - Mm,
      hist: histOf(e.blocks, N), groups,
      // размах между подгруппами: насколько признак вообще двигает этот заголовок
      span: groups.length > 1 ? Math.max(...groups.map(g => g.med)) - Math.min(...groups.map(g => g.med)) : 0 };
  });
  // при равных медианах порядок задаёт среднее, иначе наклон коромысла читался бы шумом
  // служебные в хвосте в порядке решётки страницы (headGrid.js): «без подмены», потом «нет данных»
  const svcRank = e => (!e.none ? 0 : e.t === NO_SWAP ? 1 : 2);
  list.sort((a, b) => svcRank(a) - svcRank(b) || a.med - b.med || a.mean - b.mean || b.n - a.n || a.first - b.first);
  return { list, M, Mm, n: all.length, hist: histOf(all, N) };
}

// ---- два фактора: заголовок и фраза ----
// Исход — номер блока оттока; внутри разбора считаем СРЕДНИЙ блок (медиана в клетке почти
// не шевелится). Сессии без оттока и «нет данных» отбрасываются, «без подмены» идёт служебным текстом.
// Все словари — Map: заголовок и фраза пришли снаружи.
const mean = a => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
const SEP = '\u0001';
function groupBy(rows, key, val) {
  const m = new Map();
  rows.forEach(r => { const k = key(r); if (!m.has(k)) m.set(k, []); m.get(k).push(val(r)); });
  return m;
}
const meansOf = g => { const m = new Map(); g.forEach((a, k) => m.set(k, mean(a))); return m; };

export function twoFactor(units) {
  // без оттока меры нет — отбрасываем и здесь, на случай сырого входа. «нет данных» — не текст,
  // а смесь всего, что показывалось до записи лога: уровнем фактора он съел бы эффект времени (R54).
  // «без подмены» — настоящий показанный текст (заголовок сайта), он участвует.
  const svc = new Set();
  const rows = [];
  units.forEach(u => {
    if (u.churn < 0) return;
    const h = headOf(u);
    if (h.t === NO_DATA && h.none) return;
    if (h.none) svc.add(h.t);
    rows.push({ t: h.t, ph: u.phrase, y: u.churn + 1 });
  });
  const G = mean(rows.map(r => r.y));
  const heads = [...new Set(rows.map(r => r.t))];
  const phs = [...new Set(rows.map(r => r.ph))].sort(ruCmp);
  const cellOf = groupBy(rows, r => r.t + SEP + r.ph, r => r.y);
  const byT = groupBy(rows, r => r.t, r => r.y);
  const byPh = groupBy(rows, r => r.ph, r => r.y);
  const cell = (t, ph) => { const a = cellOf.get(t + SEP + ph); return a ? { n: a.length, m: mean(a) } : null; };
  // Стандартизованный маргинал: клетки с равным весом, как будто состав заголовков у всех
  // фраз одинаковый. Разница с сырым — вклад состава.
  const stdPh = new Map(), stdT = new Map();
  phs.forEach(ph => {
    const ms = heads.map(t => cell(t, ph)).filter(Boolean).map(c => c.m);
    stdPh.set(ph, ms.length ? mean(ms) : null);
  });
  heads.forEach(t => {
    const ms = phs.map(ph => cell(t, ph)).filter(Boolean).map(c => c.m);
    stdT.set(t, ms.length ? mean(ms) : null);
  });
  // Последовательное разложение вариации: каждый шаг снимает свою часть с остатка,
  // доли неотрицательны и в сумме 100 %. Два порядка показывают общую часть факторов.
  const SST = rows.reduce((s, r) => s + Math.pow(r.y - G, 2), 0) || 1e-9;
  const seq = first => {
    const key1 = first === 't' ? (r => r.t) : (r => r.ph);
    const key2 = first === 't' ? (r => r.ph) : (r => r.t);
    const m1 = meansOf(groupBy(rows, key1, r => r.y));
    const SS1 = rows.reduce((s, r) => s + Math.pow(m1.get(key1(r)) - G, 2), 0);
    const r1 = rows.map(r => ({ ...r, e: r.y - m1.get(key1(r)) }));
    const m2 = meansOf(groupBy(r1, key2, r => r.e));
    const SS2 = r1.reduce((s, r) => s + Math.pow(m2.get(key2(r)), 2), 0);
    const r2 = r1.map(r => ({ ...r, e2: r.e - m2.get(key2(r)) }));
    const m3 = meansOf(groupBy(r2, r => r.t + SEP + r.ph, r => r.e2));
    const SS3 = r2.reduce((s, r) => s + Math.pow(m3.get(r.t + SEP + r.ph), 2), 0);
    const SSE = Math.max(0, SST - SS1 - SS2 - SS3);
    return { first, a: SS1 / SST, b: SS2 / SST, ab: SS3 / SST, e: SSE / SST };
  };
  // Аддитивная подгонка «общее + заголовок + фраза» чередованием средних — для каскада.
  const at = new Map(), bp = new Map();
  heads.forEach(t => at.set(t, 0)); phs.forEach(ph => bp.set(ph, 0));
  // строки по заголовку и фразе собраны заранее: в сегменте «вся таблица» фраз сотни
  const rowsT = groupBy(rows, r => r.t, r => r), rowsPh = groupBy(rows, r => r.ph, r => r);
  for (let it = 0; it < 30; it++) {
    rowsT.forEach((a, t) => at.set(t, mean(a.map(r => r.y - G - bp.get(r.ph)))));
    rowsPh.forEach((a, ph) => bp.set(ph, mean(a.map(r => r.y - G - at.get(r.t)))));
  }
  const headList = heads.map(t => ({
    t, none: svc.has(t), n: byT.get(t).length, m: mean(byT.get(t)), std: stdT.get(t), a: at.get(t),
    cells: phs.map(ph => ({ ph, c: cell(t, ph) })),
  })).sort((x, y) => x.m - y.m);
  const phList = phs.map(ph => ({
    ph, n: byPh.get(ph).length, m: mean(byPh.get(ph)), std: stdPh.get(ph), b: bp.get(ph),
    d: stdPh.get(ph) === null ? 0 : stdPh.get(ph) - mean(byPh.get(ph)),
  }));
  // парные сравнения внутри заголовка: только заголовки, где есть обе фразы
  const pairs = [];
  for (let i = 0; i < phs.length; i++) for (let j = i + 1; j < phs.length; j++) {
    const ds = [];
    heads.forEach(t => {
      const c1 = cell(t, phs[i]), c2 = cell(t, phs[j]);
      if (c1 && c2) ds.push({ t, d: c2.m - c1.m, n: Math.min(c1.n, c2.n) });
    });
    pairs.push({ a: phs[i], b: phs[j], k: ds.length, d: ds.length ? mean(ds.map(x => x.d)) : null,
      raw: mean(byPh.get(phs[j])) - mean(byPh.get(phs[i])), items: ds });
  }
  // разброс внутри клеток и наименьшая различимая разница — для движка трактовок
  const cellsAll = [];
  heads.forEach(t => phs.forEach(ph => { const c = cell(t, ph); if (c) cellsAll.push({ t, ph, ...c }); }));
  let ssIn = 0, dfIn = 0;
  cellOf.forEach(a => {
    if (a.length > 1) { const m = mean(a); a.forEach(v => { ssIn += (v - m) * (v - m); }); dfIn += a.length - 1; }
  });
  const sdIn = dfIn ? Math.sqrt(ssIn / dfIn) : 0;
  const nTyp = cellsAll.length ? cellsAll.map(c => c.n).sort((a, b) => a - b)[cellsAll.length >> 1] : 1;
  const mde = 2 * sdIn / Math.sqrt(Math.max(1, nTyp));
  return { G, n: rows.length, heads: headList, phs: phList, cell, cellsAll, sdIn, nTyp, mde,
    varT: seq('t'), varPh: seq('ph'), pairs,
    lo: rows.length ? rows.reduce((m, r) => Math.min(m, r.y), Infinity) : 0,
    hi: rows.reduce((m, r) => Math.max(m, r.y), 0) };
}

// ---- движок трактовок: пороги и правила поверх посчитанных чисел, без AI ----
export const SAY = {
  слабо: 0.05, заметно: 0.15,          // доля разброса: до 5 % — почти не влияет, свыше 15 % — главный
  составМал: 0.10, составБол: 0.30,    // вклад состава в блоках
  взаимМал: 0.10, взаимБол: 0.25,      // доля взаимодействия
  шумМного: 0.55,                      // доля необъяснённого
  малоВКлетке: 2,                      // сколько сессий в клетке считаем «данных нет»
};
const sayLevel = v => (v < SAY.слабо ? 'почти не влияет' : v < SAY.заметно ? 'влияет заметно' : 'главный фактор');

export function verdicts(F) {
  const nm = ph => ph;
  const pc = v => Math.round(v * 100) + ' %';
  const bl = v => (v > 0 ? '+' : '') + v.toFixed(2);
  const out = [], acts = [];
  // 1. кто главнее и что остаётся у каждого фактора, если снять вклад соседа
  const hdAlone = F.varT.a, phAlone = F.varPh.a;
  const hdNet = F.varPh.b, phNet = F.varT.b;
  const shared = Math.max(0, Math.max(hdAlone - hdNet, phAlone - phNet));
  out.push({ t: 'info', h: 'Кто двигает блок',
    s: `Сам по себе текст объясняет ${pc(hdAlone)} разброса, словосочетание — ${pc(phAlone)}. `
     + `Если сначала учесть текст, словосочетанию остаётся ${pc(phNet)}; если наоборот — тексту ${pc(hdNet)}. `
     + `Значит текст ${sayLevel(hdNet)}, словосочетание ${sayLevel(phNet)}.` });
  if (shared > 0.03)
    out.push({ t: 'warn', h: 'Часть разброса спорная',
      s: `Около ${pc(shared)} разброса нельзя честно отдать ни тексту, ни словосочетанию: они `
       + `перемешаны, потому что разным словосочетаниям достаются разные тексты. Эту часть `
       + `нельзя записывать в заслугу ни одному из них.` });
  // 2. состав искажает сырые числа
  const bad = F.phs.filter(p2 => Math.abs(p2.d) >= SAY.составМал)
    .sort((a, b2) => Math.abs(b2.d) - Math.abs(a.d));
  if (bad.length) {
    const w = bad[0];
    out.push({ t: Math.abs(w.d) >= SAY.составБол ? 'warn' : 'info', h: 'Сырые средние сдвинуты составом',
      s: `У «${nm(w.ph)}» сырое среднее ${w.m.toFixed(2)}, но после выравнивания состава — `
       + `${w.std.toFixed(2)} (${bl(w.d)} блока). Разрыв возник из-за того, какие тексты ей чаще `
       + `достаются, а не из-за самого словосочетания. `
       + (Math.abs(w.d) >= SAY.составБол ? `Сравнивать словосочетания по сырым числам здесь нельзя.`
         : `Сравнивать по сырым числам можно, но с поправкой.`) });
  } else {
    out.push({ t: 'good', h: 'Состав не мешает',
      s: `Сырые и выровненные средние по словосочетаниям расходятся меньше чем на `
       + `${SAY.составМал.toFixed(2)} блока — состав групп ровный, сырые числа можно читать как есть.` });
  }
  // 3. одинаково ли действует словосочетание у разных текстов
  const ab = F.varT.ab;
  const flip = (() => {
    // наклоны каждого текста между первой и последней фразой: разные знаки — фраза
    // одному тексту помогает, другому мешает
    const sl = F.heads.map(h => {
      const a = h.cells.filter(c => c.c);
      return a.length >= 2 ? a[a.length - 1].c.m - a[0].c.m : null;
    }).filter(v => v !== null);
    const up = sl.filter(v => v > 0.25).length, dn = sl.filter(v => v < -0.25).length;
    return { up, dn, n: sl.length };
  })();
  out.push({ t: ab >= SAY.взаимБол ? 'warn' : ab >= SAY.взаимМал ? 'info' : 'good',
    h: 'Одинаково ли работает словосочетание',
    s: ab >= SAY.взаимБол
      ? `Нет: ${pc(ab)} разброса приходится на то, что у разных текстов словосочетание работает `
        + `по-разному. У ${flip.up} текстов оно сдвигает срабатывание позже, у ${flip.dn} — раньше. `
        + `Общего правила «эта фраза лучше» не существует, работает только пара текст + фраза.`
      : ab >= SAY.взаимМал
        ? `Скорее да, но не всегда: ${pc(ab)} разброса — это расхождение между текстами. `
          + `У ${flip.up} текстов сдвиг позже, у ${flip.dn} — раньше.`
        : `Да: расхождение между текстами всего ${pc(ab)}, словосочетание действует на все тексты `
          + `примерно одинаково, и его эффект можно считать одним числом.` });
  // 4. хватает ли данных
  const small = F.cellsAll.filter(c => c.n <= SAY.малоВКлетке).length;
  const empty = F.heads.length * F.phs.length - F.cellsAll.length;
  if (F.varT.e >= SAY.шумМного || small + empty > F.heads.length * F.phs.length * 0.3)
    out.push({ t: 'warn', h: 'Данных мало',
      s: `${pc(F.varT.e)} разброса — это различия внутри одинаковых групп, то есть случайность. `
       + (small + empty ? `На ${small + empty} сочетаниях из ${F.heads.length * F.phs.length} `
         + `сессий почти нет. ` : '')
       + `Разницу меньше ${F.mde.toFixed(2)} блока сейчас считать настоящей нельзя — `
       + `нужно набрать больше сессий.` });
  // 5. кто раньше, кто позже — по выровненным числам
  const ord = [...F.phs].filter(p2 => p2.std !== null).sort((a, b2) => a.std - b2.std);
  if (ord.length >= 2)
    out.push({ t: 'info', h: 'Кто срабатывает раньше',
      s: `После выравнивания состава раньше всех — «${nm(ord[0].ph)}» (${ord[0].std.toFixed(2)}), `
       + `позже всех — «${nm(ord[ord.length - 1].ph)}» (${ord[ord.length - 1].std.toFixed(2)}). `
       + `Разрыв ${(ord[ord.length - 1].std - ord[0].std).toFixed(2)} блока`
       + ((ord[ord.length - 1].std - ord[0].std) < F.mde
         ? `, что меньше различимой разницы — считайте их одинаковыми.` : `.`) });
  const hOrd = [...F.heads].sort((a, b2) => a.m - b2.m);
  if (hOrd.length >= 2)
    out.push({ t: 'info', h: 'Какие формулировки быстрее',
      s: `Раньше всех срабатывает «${hOrd[0].t}» (${hOrd[0].m.toFixed(2)}), позже всех — `
       + `«${hOrd[hOrd.length - 1].t}» (${hOrd[hOrd.length - 1].m.toFixed(2)}). `
       + `Разрыв ${(hOrd[hOrd.length - 1].m - hOrd[0].m).toFixed(2)} блока.` });
  // 6. что делать: чистые вклады сравниваются друг с другом, а не только с порогом
  const hSpan = hOrd.length >= 2 ? hOrd[hOrd.length - 1].m - hOrd[0].m : 0;
  const pSpan = ord.length >= 2 ? ord[ord.length - 1].std - ord[0].std : 0;
  if (hdNet < SAY.слабо && phNet < SAY.слабо)
    acts.push(`Ни текст, ни словосочетание заметно не двигают срабатывание — на этих данных `
      + `перебирать их бессмысленно, причина в чём-то третьем: канал, момент показа, аудитория.`);
  else if (hdNet >= phNet * 2)
    acts.push(`Тестируйте формулировки, а не словосочетания: смена текста двигает срабатывание `
      + `на ${hSpan.toFixed(1)} блока, смена словосочетания — на ${pSpan.toFixed(1)}. `
      + `На перебор фраз время тратить не стоит.`);
  else if (phNet >= hdNet * 2)
    acts.push(`Тестируйте словосочетания: они двигают срабатывание на ${pSpan.toFixed(1)} блока, `
      + `а формулировка — на ${hSpan.toFixed(1)}.`);
  else
    acts.push(`Влияют оба примерно поровну: меняйте по одному за раз, иначе не поймёте, что сработало.`);
  if (ab >= SAY.взаимБол) {
    let best = null, worst = null;
    F.heads.forEach(h => h.cells.forEach(c => {
      if (!c.c || c.c.n <= SAY.малоВКлетке) return;
      if (!best || c.c.m < best.m) best = { t: h.t, ph: c.ph, m: c.c.m, n: c.c.n };
      if (!worst || c.c.m > worst.m) worst = { t: h.t, ph: c.ph, m: c.c.m, n: c.c.n };
    }));
    if (best && worst)
      acts.push(`Выбирайте не фразу и не текст по отдельности, а связку. Быстрее всех — `
        + `«${best.t}» + «${nm(best.ph)}» (${best.m.toFixed(1)}, ${best.n} сессий), медленнее всех — `
        + `«${worst.t}» + «${nm(worst.ph)}» (${worst.m.toFixed(1)}, ${worst.n}).`);
  }
  if (bad.length && Math.abs(bad[0].d) >= SAY.составБол)
    acts.push(`Не сравнивайте словосочетания по сводным числам из отчёта: состав групп разный. `
      + `Сравнивайте внутри одинаковых текстов — это вкладка «пары».`);
  if (F.varT.e >= SAY.шумМного)
    acts.push(`Не принимайте решение по разнице меньше ${F.mde.toFixed(2)} блока: на текущем объёме `
      + `это в пределах случайности. Либо копите данные, либо сравнивайте только крайние варианты.`);
  if (F.pairs.some(p2 => p2.d !== null && Math.abs(p2.raw - p2.d) >= SAY.составМал))
    acts.push(`Разница «в парах» и «сырое» расходятся — в отчётах по словосочетаниям берите парную, `
      + `она не зависит от того, каких текстов сколько попало в выборку.`);
  return { out, acts };
}

// ---- события ----
// Короткие месяцы везде в слоях, как в П (MONTHS 2397): «15 янв», ось «янв, фев».
const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
// day — московские сутки от эпохи, поэтому UTC-дата day·сутки и есть московская дата
export const dayLabel = day => { const d = new Date(day * 864e5); return d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()]; };
export const monthLabel = day => MONTHS[new Date(day * 864e5).getUTCMonth()];
function plural(n, f) {
  const a = Math.abs(n) % 100, b = a % 10;
  return a > 10 && a < 20 ? f[2] : b > 1 && b < 5 ? f[1] : b === 1 ? f[0] : f[2];
}
const DAYS = ['день', 'дня', 'дней'];
const SES = ['сессия', 'сессии', 'сессий'];

// Только то, что считается из реальных данных (таблица «События» в спеке).
// opts.convTypes — имена битов convMask; opts.ruleChanges — [{at: ms, note?}] даты снимков,
// после которых сменился заголовок у фраз столбца (их находит вызывающий).
// i — индекс в хронологическом ряду, sid — id сессии: ряд в сцене бывает пересортирован.
export function deriveEvents(units, opts = {}) {
  const list = orderByTime(units);
  if (!list.length) return [];
  const convTypes = opts.convTypes || [];
  const out = [];
  const push = (kind, i, title, note, gap = null, day = list[i].day) =>
    out.push({ kind, at: gap ? 'gap' : 'in', i, gap, day, sid: list[i].id, sid0: gap ? list[gap[0]].id : null, title, note });

  // конверсии и пороги накопленного числа конверсионных сессий
  const convIdx = [];
  list.forEach((u, i) => { if (u.convMask) convIdx.push(i); });
  convIdx.forEach(i => {
    const u = list[i];
    const names = convTypes.filter((_, k) => u.convMask & (1 << k));
    const day = u.convFirst ? dayOf(u.convFirst) : u.day;
    push('conv', i, 'Конверсия: ' + (names.join(', ') || 'тип неизвестен'),
      `Сессия №${i + 1} ${dayLabel(day)}, фраза «${u.phrase}».`, null, day);
  });
  const total = convIdx.length;
  const TH = [25, 60];
  let ti = 0;
  convIdx.forEach((i, k) => {
    const acc = k + 1;
    while (ti < TH.length && acc * 100 >= TH[ti] * total - 1e-9) {
      const days = list[i].day - list[0].day;
      push('mile', i, `${TH[ti]} % конверсий`,
        `На сессии №${i + 1} набрано ${acc} из ${total} конверсионных сессий столбца `
        + `(${Math.round(acc / total * 100)} %), через ${days} ${plural(days, DAYS)} после первой сессии.`);
      ti++;
    }
  });

  // смена фразы: не больше двух, и новая фраза держится хотя бы две сессии подряд
  let prevPh = list[0].phrase, chgN = 0;
  for (let i = 1; i < list.length && chgN < 2; i++) {
    const ph = list[i].phrase;
    if (ph !== prevPh && i + 1 < list.length && list[i + 1].phrase === ph) {
      chgN++;
      push('chg', i, 'Смена фразы: ' + ph,
        `С сессии №${i + 1} пошли сессии по «${ph}» вместо «${prevPh}» — и держатся дальше подряд.`, [i - 1, i]);
    }
    prevPh = ph;
  }

  // смена правил балансировщика: встаёт в разрыв между последней сессией до снимка и первой после
  (opts.ruleChanges || []).forEach(rc => {
    const i = list.findIndex(u => u.start > rc.at);
    if (i <= 0) return;
    push('chg', i, 'Смена правил балансировщика',
      rc.note || `${dayLabel(dayOf(rc.at))} сменились правила, заголовки у фраз столбца другие.`,
      [i - 1, i], dayOf(rc.at));
  });

  // самая длинная пауза от 30 дней
  let gi = -1, gmax = 0;
  for (let i = 1; i < list.length; i++) {
    const g = list[i].day - list[i - 1].day;
    if (g > gmax) { gmax = g; gi = i; }
  }
  if (gmax >= 30 && gi > 0)
    push('mile', gi, `Пауза ${gmax} ${plural(gmax, DAYS)}`,
      `После сессии №${gi} (${dayLabel(list[gi - 1].day)}) пауза ${gmax} ${plural(gmax, DAYS)} — `
      + `самая длинная в столбце.`, [gi - 1, gi]);

  // самый плотный день
  const byDay = new Map();
  list.forEach(u => byDay.set(u.day, (byDay.get(u.day) || 0) + 1));
  let bd = null, bn = 1;
  byDay.forEach((n, d) => { if (n > bn) { bn = n; bd = d; } });
  if (bd !== null)
    push('mile', list.findIndex(u => u.day === bd), `${bn} ${plural(bn, SES)} за день`,
      `${dayLabel(bd)} — самый плотный день периода: ${bn} ${plural(bn, SES)}.`);

  out.sort((a, b) => a.day - b.day || a.i - b.i);
  out.forEach((e, k) => { e.id = k; });
  return out;
}

// Стыки между соседними сессиями ряда (по времени): сдвиг блока оттока и пауза.
// Обе величины нормируются своим максимумом и складываются, берутся девять самых весомых.
// Сессия без оттока в пары не берётся: у неё нет блока, подпись «блок 0» была бы ложью.
export function joints(units) {
  const cand = [];
  for (let i = 1; i < units.length; i++) {
    const h1 = hitOf(units[i - 1]), h2 = hitOf(units[i]);
    if (h1 < 0 || h2 < 0) continue;
    const d = h2 - h1;                               // + позже, − раньше
    const pause = units[i].day - units[i - 1].day;
    if (!d && pause < 2) continue;
    cand.push({ i, d, pause, hb: h1 + 1, hb2: h2 + 1 });
  }
  const mD = cand.reduce((m, c) => Math.max(m, Math.abs(c.d)), 1);
  const mP = cand.reduce((m, c) => Math.max(m, c.pause), 1);
  cand.forEach(c => { c.w = Math.abs(c.d) / mD + c.pause / mP; });
  return cand.sort((x, y) => y.w - x.w).slice(0, 9).sort((x, y) => x.i - y.i);
}
